import React, { useState, useEffect, useRef } from "react";
import { ASSETS } from "@/lib/links";

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function shouldSkipVideoForNetwork() {
  const conn =
    navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  if (!conn) return false;
  if (conn.saveData) return true;
  const type = conn.effectiveType;
  return type === "slow-2g" || type === "2g";
}

export default function VideoBackground({
  children,
  className = "",
  overlayClassName = "bg-black/70",
  showGrid = true,
}) {
  const heroVideoUrl = ASSETS.heroVideo;
  const [ready, setReady] = useState(false);
  const [mountVideo, setMountVideo] = useState(false);
  const [videoPlaying, setVideoPlaying] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const id = requestAnimationFrame(() => setReady(true));
    return () => cancelAnimationFrame(id);
  }, []);

  useEffect(() => {
    if (!heroVideoUrl) return undefined;
    if (prefersReducedMotion() || shouldSkipVideoForNetwork()) return undefined;

    const start = () => setMountVideo(true);
    const schedule =
      typeof window.requestIdleCallback === "function"
        ? (cb) => window.requestIdleCallback(cb, { timeout: 900 })
        : (cb) => setTimeout(cb, 200);
    const cancel =
      typeof window.cancelIdleCallback === "function"
        ? window.cancelIdleCallback
        : clearTimeout;
    const id = schedule(start);
    return () => cancel(id);
  }, [heroVideoUrl]);

  useEffect(() => {
    if (!mountVideo) return undefined;
    const el = videoRef.current;
    if (!el) return undefined;

    let softPaused = false;

    const tryPlay = () => {
      const p = el.play();
      if (p && typeof p.catch === "function") p.catch(() => {});
    };

    const onPlaying = () => {
      softPaused = false;
      setVideoPlaying(true);
    };

    const onWaiting = () => {
      setVideoPlaying(false);
      softPaused = true;
    };

    const onCanPlay = () => {
      if (document.visibilityState === "visible") tryPlay();
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        el.pause();
        return;
      }
      tryPlay();
    };

    const onConnChange = () => {
      if (shouldSkipVideoForNetwork()) {
        el.pause();
        setVideoPlaying(false);
        setMountVideo(false);
        return;
      }
      if (!softPaused && document.visibilityState === "visible") tryPlay();
    };

    el.addEventListener("playing", onPlaying);
    el.addEventListener("waiting", onWaiting);
    el.addEventListener("canplay", onCanPlay);
    document.addEventListener("visibilitychange", onVisibility);

    const conn =
      navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    conn?.addEventListener?.("change", onConnChange);

    tryPlay();

    return () => {
      el.removeEventListener("playing", onPlaying);
      el.removeEventListener("waiting", onWaiting);
      el.removeEventListener("canplay", onCanPlay);
      document.removeEventListener("visibilitychange", onVisibility);
      conn?.removeEventListener?.("change", onConnChange);
    };
  }, [mountVideo]);

  return (
    <div className={`relative min-h-screen bg-[#030712] overflow-x-hidden max-w-[100vw] ${className}`}>
      <div
        className="fixed inset-0 z-[100] pointer-events-none"
        style={{
          background: "#030712",
          opacity: ready ? 0 : 1,
          visibility: ready ? "hidden" : "visible",
          transition: "opacity 0.35s ease, visibility 0.35s ease",
        }}
      />

      <div className="fixed inset-0 z-0 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center bg-no-repeat scale-105 transition-opacity duration-1000 ease-out"
          style={{
            backgroundImage: `url(${ASSETS.videoPoster})`,
            filter: "brightness(0.4) saturate(0.9)",
            opacity: videoPlaying ? 0 : 1,
          }}
          aria-hidden="true"
        />

        {mountVideo && heroVideoUrl ? (
          <video
            ref={videoRef}
            muted
            loop
            playsInline
            autoPlay
            preload="auto"
            poster={ASSETS.videoPoster}
            data-testid="site-video-bg"
            className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ease-out"
            style={{ opacity: videoPlaying ? 1 : 0 }}
          >
            <source src={heroVideoUrl} type="video/mp4" />
          </video>
        ) : null}

        <div className={`absolute inset-0 ${overlayClassName}`} />
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            background:
              "radial-gradient(ellipse at center, transparent 0%, rgba(3,7,18,0.35) 70%, rgba(3,7,18,0.85) 100%)",
          }}
        />
        {showGrid ? (
          <div
            className="absolute inset-0 opacity-30"
            style={{
              backgroundImage:
                "linear-gradient(to right, rgba(0,163,255,0.03) 1px, transparent 1px), linear-gradient(to bottom, rgba(0,163,255,0.03) 1px, transparent 1px)",
              backgroundSize: "60px 60px",
            }}
          />
        ) : null}
      </div>

      <div className="relative z-10 overflow-x-hidden isolate">{children}</div>
    </div>
  );
}
